import { useState, useRef } from "react";
import { motion } from "framer-motion";
import { CheckCheck, Square, ImageDown, Volume2, Loader2, Trash2 } from "lucide-react";
import { answerCardUrl, messageAudioUrl } from "@/lib/api";
import InvoiceCard from "@/components/InvoiceCard";

export function FormattedText({ text }) {
  const parts = String(text).replaceAll("**", "*").split(/(\*[^*]+\*)/g);
  return (
    <span>
      {parts.map((p, i) =>
        p.startsWith("*") && p.endsWith("*") && p.length > 2 ? (
          <strong key={`seg-${i}`}>{p.slice(1, -1)}</strong>
        ) : (
          <span key={`seg-${i}`}>{p}</span>
        )
      )}
    </span>
  );
}

function VoiceButton({ messageId }) {
  const [audioState, setAudioState] = useState("idle");
  const audioRef = useRef(null);

  const toggleAudio = () => {
    if (audioState === "playing") {
      audioRef.current?.pause();
      setAudioState("idle");
      return;
    }
    setAudioState("loading");
    const a = new Audio(messageAudioUrl(messageId));
    audioRef.current = a;
    a.onended = () => setAudioState("idle");
    a.onerror = () => setAudioState("idle");
    a.play().then(() => setAudioState("playing")).catch(() => setAudioState("idle"));
  };

  return (
    <button
      data-testid="voice-reply-btn"
      onClick={toggleAudio}
      title="Jawab suno"
      className="flex items-center gap-1.5 text-[11px] font-semibold text-gray-500 border border-gray-300 px-2.5 py-1 rounded-full hover:border-[#006D5B] hover:text-[#006D5B] transition-colors duration-150"
    >
      {audioState === "loading" ? <Loader2 size={12} className="animate-spin" /> : audioState === "playing" ? <Square size={11} /> : <Volume2 size={12} />}
      {audioState === "playing" ? "Ruko" : "Suno"}
    </button>
  );
}

export default function Bubble({ msg, botType, onDelete }) {
  const isUser = msg.role === "user";
  const cardId = msg.message_id || msg.id;
  const showCard = !isUser && botType === "tutor" && msg.kind === "text" && cardId;
  const showVoice = !isUser && cardId && (msg.text || "").trim() && msg.kind !== "paywall";

  return (
    <motion.div
      initial={{ opacity: 0, y: 14 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25 }}
      className={`flex ${isUser ? "justify-end" : "justify-start"}`}
    >
      <div
        data-testid={isUser ? "chat-message-user" : "chat-message-bot"}
        className={`max-w-[85%] px-3 py-2 text-[13px] leading-relaxed shadow-sm rounded-2xl whitespace-pre-wrap ${
          isUser ? "bg-[#DCF8C6] rounded-tr-none" : "bg-white rounded-tl-none"
        }`}
      >
        {msg.text && <FormattedText text={msg.text} />}
        {msg.kind === "invoice" && msg.invoice && <InvoiceCard invoice={msg.invoice} />}
        <div className="flex items-center gap-1.5 mt-2">
          {showVoice && <VoiceButton messageId={cardId} />}
          {showCard && (
            <button
              data-testid="answer-card-btn"
              onClick={() => window.open(answerCardUrl(cardId), "_blank", "noopener")}
              className="flex items-center gap-1.5 text-[11px] font-semibold text-[#3730A3] border border-[#3730A3]/30 px-2.5 py-1 rounded-full hover:bg-[#3730A3]/5 transition-colors duration-150"
            >
              <ImageDown size={12} /> Share as card
            </button>
          )}
        </div>
        <div className="flex justify-end items-center gap-1.5 mt-0.5">
          {onDelete && cardId && msg.kind !== "greeting" && (
            <button data-testid="chat-message-delete-btn" onClick={() => onDelete(msg)} title="Message delete karo"
              className="text-gray-300 hover:text-red-400 transition-colors duration-150">
              <Trash2 size={10} />
            </button>
          )}
          <span className="text-[9px] text-gray-400">
            {new Date(msg.created_at || Date.now()).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}
          </span>
          {isUser && <CheckCheck size={12} className="text-[#53BDEB]" />}
        </div>
      </div>
    </motion.div>
  );
}
